import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Image } from '@sas/nova-commons/components/Image';
import { setConstraints } from '../../store/birdLayout/BirdLayoutState';
import { ImageState } from '../../store/birdElement/imageState';
import { useDebounce } from '../hooks';
import { selectAccessibilityLabelState, selectBirdImageState, } from '../../store/selectors';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Subject } from 'rxjs';
import { useI18n } from '../../locale/I18nContext';
const imageCache = new Map();
function setCache(url, value) {
    let info = imageCache.get(url);
    if (!info) {
        info = {
            value,
            subject: new Subject(),
        };
        imageCache.set(url, info);
    }
    info.value = value;
    info.subject.next(value);
    return info;
}
function fetchImage(url) {
    fetch(url, { credentials: 'include' })
        .then((resp) => resp.blob())
        .then((data) => {
        setCache(url, { dataUrl: URL.createObjectURL(data) });
    })
        .catch(() => {
        setCache(url, { error: true });
    });
}
export const BirdImageBase = function (props) {
    const [visible, setVisible] = useState(false);
    const [imageSrc, setImageSrc] = useState(undefined);
    const { mode, url, alt, isExternal } = props.state;
    const defaultAccessibilityLabel = useI18n({ key: 'BirdImage.image.txt' });
    // always fetch because of cors
    useEffect(() => {
        if (!url) {
            setImageSrc('');
            return;
        }
        if (isExternal) {
            setImageSrc(url);
            return;
        }
        // Fetch non-external images so we can include credentials in the request
        let imageData = imageCache.get(url);
        if (!imageData) {
            imageData = setCache(url, {});
            fetchImage(url);
        }
        // If the cache has an error, try again.
        if (imageData.value.error) {
            fetchImage(url);
        }
        if (imageData.value.dataUrl) {
            setImageSrc(imageData.value.dataUrl);
        }
        const sub = imageData === null || imageData === void 0 ? void 0 : imageData.subject.subscribe((val) => {
            if (val.dataUrl) {
                setImageSrc(val.dataUrl);
            }
            else if (val.error) {
                props.actions.update(props.id, { loadState: 'error' });
            }
        });
        return () => sub.unsubscribe();
    }, [url, isExternal, props.id, props.actions]);
    const onLoad = (e) => {
        const target = e.currentTarget;
        props.actions.setConstraints(props.id, {
            // Minimum width and height is 10 which matches with VAN
            minHeight: 10,
            minWidth: 10,
            preferredHeight: target.naturalHeight,
            preferredWidth: target.naturalWidth,
        });
        props.actions.update(props.id, { loadState: 'success' });
        setVisible(true);
    };
    // useDebounce added because of ReportViewer code: S1280517: prevent multiple clicks from happening
    const [clickLocation, setClickLocation] = useDebounce(undefined, 300);
    useEffect(() => {
        const notifyClick = props.actions.notifyClick;
        if (!clickLocation) {
            return;
        }
        notifyClick(props.id, clickLocation);
        setClickLocation(undefined, true);
    }, [clickLocation, props.id, props.actions.notifyClick, setClickLocation]);
    const refContainer = useRef(null);
    return (_jsx("div", Object.assign({ ref: refContainer, style: {
            overflow: 'auto',
            width: '100%',
            height: '100%',
            position: 'relative',
            display: visible ? 'flex' : 'none',
            flexShrink: 1,
        } }, { children: _jsx(Image, { style: { position: 'absolute' }, tooltip: alt !== null && alt !== void 0 ? alt : undefined, mode: mode, src: imageSrc !== null && imageSrc !== void 0 ? imageSrc : '', alt: props.accessibilityLabel || defaultAccessibilityLabel, onLoad: onLoad, onError: () => {
                if (!imageSrc) {
                    return;
                }
                props.actions.update(props.id, { loadState: 'error' });
            }, onClick: (e) => setClickLocation({ x: e.clientX, y: e.clientY }), onPress: () => {
                var _a, _b;
                // Set click location to the top-left corner in case this was triggered
                // by a keyboard interaction. onClick is called first, so useDebounce
                // will ignore this if the source was a mouse click.
                const { x = 0, y = 0 } = (_b = (_a = refContainer.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) !== null && _b !== void 0 ? _b : {};
                setClickLocation({ x, y });
            } }) })));
};
export const BirdImage = connect((state, props) => ({
    state: selectBirdImageState(state, props.id),
    accessibilityLabel: selectAccessibilityLabelState(state, props.id),
}), (dispatch) => ({
    actions: bindActionCreators(Object.assign({ setConstraints }, ImageState.actions), dispatch),
}))(BirdImageBase);
